import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnInit, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';
import { TagService } from '../../services/api/tags.service';

@Component({
    selector: 'app-tags-input',
    templateUrl: './tags-input.component.html',
    styleUrls: ['./tags-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagsInputComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TagsInputComponent implements ControlValueAccessor, OnInit {
  private _tags: string[] = [];
  private isInputDisabled = false;
  private isFormDisabled = false;
  private onChange: (tags: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  @Input()
  set tags(value: string[] | null) {
    this._tags = [...(value ?? [])];
  }

  get tags(): string[] {
    return this._tags;
  }

  @Input()
  set disabled(value: boolean) {
    this.isInputDisabled = !!value;
    this.syncInputDisabledState();
  }

  get disabled(): boolean {
    return this.isInputDisabled || this.isFormDisabled;
  }

  tagInput = new FormControl();
  filteredSuggestions: Observable<string[]>;
  showInput = false;
  loading = false;

  @ViewChild('inputElement', { static: false }) inputElement: ElementRef;
  @ViewChild('container', { static: false }) containerElement: ElementRef;
  @ViewChild(MatAutocompleteTrigger, { static: false }) autocompleteTrigger: MatAutocompleteTrigger;

  constructor(private readonly tagService: TagService,
    private readonly changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.syncInputDisabledState();
    this.filteredSuggestions = this.tagInput.valueChanges.pipe(
      debounceTime(300),
      startWith(''),
      tap(() => this.loading = true),
      switchMap(value => this.tagService.getByKey(value)),
      map(response => Array.isArray(response.tags) ? response.tags.filter(tag => !this.tags.includes(tag)) : []),
      tap(() => this.loading = false)
    );    
  }

  addTag() {
    if (this.disabled || !this.tagInput.value) {
      return;
    }

    const inputVal = this.tagInput.value.trim();

    if (inputVal && !this.tags.includes(inputVal)) {
      this.updateTags([...this.tags, inputVal]);
      this.tagInput.setValue('');
    }
    
    this.autocompleteTrigger.openPanel();
  }

  removeTag(tag: string) {
    if (this.disabled) {
      return;
    }

    this.updateTags(this.tags.filter(t => t !== tag));
    this.tagInput.setValue(this.tagInput.value);
  }

  onTagSelected(event: any) {
    if (this.disabled) {
      return;
    }

    const tag = event.option.value;
    if (tag && !this.tags.includes(tag)) {
      this.updateTags([...this.tags, tag]);
    }
    this.tagInput.setValue('');
    this.focusInput();
  }

  focusInput() {
    if (this.inputElement && this.inputElement.nativeElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  openInput(event?: MouseEvent) {
    event?.stopPropagation();

    if (this.disabled) {
      return;
    }

    this.showInput = true;
    this.changeDetector.detectChanges();
    this.focusInput();
    setTimeout(() => {
      this.autocompleteTrigger?.openPanel();
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const containerEl = this.containerElement?.nativeElement as HTMLElement | undefined;

    if (!containerEl) {
      return;
    }

    const clickedInside = containerEl.contains(target);
    if (!clickedInside && this.showInput) {
      this.showInput = false;
      this.changeDetector.detectChanges();
    }
  }

  writeValue(tags: string[] | null): void {
    this.tags = tags;
  }

  registerOnChange(fn: (tags: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isFormDisabled = isDisabled;
    this.syncInputDisabledState();
  }

  private updateTags(tags: string[]): void {
    this.tags = tags;
    this.onTouched();
    this.onChange([...this.tags]);
  }

  private syncInputDisabledState(): void {
    this.disabled
      ? this.tagInput.disable({ emitEvent: false })
      : this.tagInput.enable({ emitEvent: false });
  }
}
